import { Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Storefront, Package, ListBullets, Gear, SignOut, House } from '@phosphor-icons/react';
import SellerNewOrderAlert from '@/components/seller/SellerNewOrderAlert';

export default function SellerLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [initial, setInitial] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'seller') { setInitial(false); return; }
    (async () => {
      try {
        const { data } = await api.get('/sellers/me');
        setSeller(data);
      } catch (e) { setSeller(null); }
      finally { setInitial(false); }
    })();
  }, [user]);

  if (loading || initial) return <div className="min-h-screen grid place-items-center bg-cream-100 text-stone-500">Loading…</div>;
  if (!user || user.role !== 'seller') return <Navigate to="/seller/login" replace />;

  const pending = seller?.status !== 'approved';
  const link = ({ isActive }) => `flex items-center gap-2 h-10 px-3 rounded-xl text-sm ${isActive ? 'bg-terracotta-600 text-white' : 'text-stone-700 hover:bg-cream-100'}`;

  return (
    <div className="min-h-screen flex bg-cream-100" data-testid="seller-layout">
      <SellerNewOrderAlert enabled={seller?.status === 'approved'} />
      <aside className="w-60 bg-white border-r border-stone-200 flex flex-col">
        <div className="p-4 border-b border-stone-200 flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-olive-700 text-white grid place-items-center"><Storefront size={18}/></span>
          <div className="min-w-0">
            <p className="font-heading text-sm text-stone-900 truncate">{seller?.shop_name || 'My Store'}</p>
            <p className="text-[10px] uppercase tracking-wider text-stone-500">{seller?.status || '—'}</p>
          </div>
        </div>
        <nav className="p-2 flex-1 space-y-1">
          <NavLink to="/seller/dashboard" className={link} data-testid="seller-nav-dashboard"><House size={16}/> Dashboard</NavLink>
          <NavLink to="/seller/products" className={link} data-testid="seller-nav-products"><Package size={16}/> Products</NavLink>
          <NavLink to="/seller/orders" className={link} data-testid="seller-nav-orders"><ListBullets size={16}/> Orders</NavLink>
          <NavLink to="/seller/settings" className={link} data-testid="seller-nav-settings"><Gear size={16}/> Store settings</NavLink>
        </nav>
        <div className="p-2 border-t border-stone-200 space-y-1">
          <a href="/" className="flex items-center gap-2 h-10 px-3 rounded-xl text-xs text-stone-600 hover:bg-cream-100"><Storefront size={14}/> View storefront</a>
          <button onClick={() => { logout(); navigate('/seller/login'); }} className="flex items-center gap-2 h-10 px-3 rounded-xl text-xs text-stone-600 hover:bg-cream-100 w-full"><SignOut size={14}/> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        {pending && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-6 py-3" data-testid="seller-pending-banner">
            Your store is <b>{seller?.status || 'pending'}</b>. {seller?.status === 'rejected' ? `Reason: ${seller.rejected_reason || 'contact admin'}` : 'An admin will approve it shortly. You can still prepare your store below.'}
          </div>
        )}
        <div className="p-6"><Outlet context={{ seller, refreshSeller: async () => {
          try { const { data } = await api.get('/sellers/me'); setSeller(data); } catch {}
        }}}/></div>
      </main>
    </div>
  );
}
